import React, {Fragment, ReactNode, useEffect, useRef, useState} from 'react';
import {
  FlatList,
  Keyboard,
  Modal,
  Platform,
  ScrollView,
  View,
} from 'react-native';
import Animated, {
  SlideInDown,
  SlideInLeft,
  SlideOutDown,
  SlideOutRight,
} from 'react-native-reanimated';
import {useFocusEffect} from '@react-navigation/native';
import axios from 'axios';
import {observer} from 'mobx-react-lite';
import {useDebounce} from 'use-debounce';

import BarcodeScanner from '../../components/BarcodeScanner';
import GiftCardTileAbbreviatedHorizontal from '../../components/GiftCardTileAbbreviatedHorizontal';
import {
  BarcodeIconButton,
  CircleXMarkIconButton,
  SearchIconButton,
} from '../../components/icons';
import ProductTileAbbreviatedHorizontal from '../../components/ProductTileAbbreviatedHorizontal';
import PromotionSearchAbbreviatedTile from '../../components/PromotionSearchAbbreviatedTile';
import SearchBrandAbbreviatedTile from '../../components/SearchBrandAbbreviatedTile';
import SearchDiscoverAbbreviatedTile from '../../components/SearchDiscoverAbbreviatedTile';
import TextInput from '../../components/ui-controls/form/TextInput';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import config from '../../config';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import apiService from '../../transport/apiService';

import styles from './styles';

const GIFT_CARD_RESULT_TYPE = 'gift-card';

const FullScreenMessageView = ({children}: {children: ReactNode}) => (
  <View
    style={{
      justifyContent: 'flex-end',
      alignItems: 'center',
      height: 200,
    }}>
    <Text
      style={{
        fontSize: 16,
        fontWeight: '500',
        color: theme.colour.text.heading.primary,
      }}>
      {children}
    </Text>
  </View>
);

type SearchBarRightIconsProps = {
  onClearSearchPress: () => void;
  onScanBarcodePress: () => void;
  isCircleXMarkIconButtonVisible: boolean;
  isBarcodeIconButtonVisible: boolean;
};
const SearchBarRightIcons = ({
  onClearSearchPress,
  onScanBarcodePress,
  isCircleXMarkIconButtonVisible,
  isBarcodeIconButtonVisible,
}: SearchBarRightIconsProps) => (
  <View style={{flexDirection: 'row', marginLeft: 4}}>
    {!!isCircleXMarkIconButtonVisible && (
      <CircleXMarkIconButton onPress={onClearSearchPress} />
    )}
    {!!isBarcodeIconButtonVisible && Platform.OS !== 'web' && (
      <BarcodeIconButton onPress={onScanBarcodePress} />
    )}
  </View>
);

type SearchBarProps = {
  editable: boolean;
  onManualSearchPress: () => void;
  onScanBarcodePress: () => void;
  onClearSearchPress: () => void;
  onSubmitEditing: ({nativeEvent}: {nativeEvent: {text: string}}) => void;
  onTextChanged: (text: string) => void;
  value: string;
};

const SearchBar = ({
  editable,
  onManualSearchPress,
  onScanBarcodePress,
  onClearSearchPress,
  onSubmitEditing,
  onTextChanged,
  value,
}: SearchBarProps) => {
  const inputRef = useRef(null);

  useFocusEffect(
    React.useCallback(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        // This hack is needed because autofocus doesn't work properly
        // on web, giving inconsistent behaviour between first mount and otherwise
        setTimeout(() => {
          inputRef.current.focus();
        }, 1);
      }
      return () => null;
    }, []),
  );

  return (
    <View style={styles.searchBarHolder}>
      <View style={styles.searchBar}>
        <TextInput
          ref={inputRef}
          autoCorrect={false}
          autoFocus
          dataDetectorTypes="none"
          editable={editable}
          leftIcon={<SearchIconButton onPress={onManualSearchPress} />}
          maxLength={80}
          onChangeText={onTextChanged}
          onSubmitEditing={onSubmitEditing}
          placeholder="What are you looking for?"
          placeholderTextColor="lightgrey"
          returnKeyType="search"
          rightIcon={
            <SearchBarRightIcons
              isBarcodeIconButtonVisible={!value}
              isCircleXMarkIconButtonVisible={!!value}
              onClearSearchPress={onClearSearchPress}
              onScanBarcodePress={onScanBarcodePress}
            />
          }
          spellCheck={false}
          style={styles.textInput}
          underlineColorAndroid="transparent"
          value={value}
        />
      </View>
    </View>
  );
};

// Poor man's request queue
let reqCount = 0;
let cancelToken;

const SearchScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.NEW_SEARCH>) => {
    const {
      globalStore,
      brandsStore,
      promotionsStore: activationsStore,
      feedStore,
    } = useStores();
    // Text input
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText] = useDebounce(searchText, 300);
    // MFL search results
    const [results, setResults] = useState<any[]>([]);
    const [selectedSearchResult, setSelectedSearchResult] = useState(null);
    // Secondary search results
    const [brand, setBrand] = useState(null);
    const [activations, setActivations] = useState([]);
    const [discoverArticles, setDiscoverArticles] = useState([]);
    // Barcode scan
    const [scanning, setScanning] = useState(false);
    const [scannedBarcodeValue, setScannedBarcodeValue] = useState('');
    // No products found
    const [hasSearched, setHasSearched] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isBarcode, setIsBarcode] = useState(false);

    useEffect(() => {
      if (debouncedSearchText) {
        searchMFLKeywords(debouncedSearchText);
      }
    }, [debouncedSearchText]);

    useEffect(() => {
      // we want to clear the search results when we navigate to the search screen
      // but we want to preserve them if we're coming 'back' from a product that we found
      // initially while searching, or any other 'back' button in the app
      if (globalStore.isFromNavigationSearch) {
        setSelectedSearchResult(null);
        setSearchText('');
        setResults([]);
        setBrand(null);
        setActivations([]);
        setDiscoverArticles([]);
        setScannedBarcodeValue('');
        setHasSearched(false);
        setIsSearching(false);
        setIsBarcode(false);

        globalStore.setIsFromNavigationSearch(false);
        return;
      }

      if (!selectedSearchResult) {
        setBrand(null);
        setActivations([]);
        setDiscoverArticles([]);
        return;
      }
      // Get any matching brands.
      const matchedBrand = brandsStore.brands.filter(
        b =>
          b.brand.toLowerCase() ===
          selectedSearchResult?.brand_name?.toLowerCase(),
      )[0];
      setBrand(matchedBrand);
      let additional = !!matchedBrand;

      // Matching activations
      setActivations(
        activationsStore.promotions.filter(activation =>
          activation.barcodes?.some(bc => {
            if (bc.padStart(14, '0') === selectedSearchResult.product_code) {
              additional = true;
              return true;
            }
          }),
        ),
      );
      // Articles
      setDiscoverArticles(
        feedStore.discoverList.filter(article => {
          article.productBarcodes?.some(bc => {
            if (bc.padStart(14, '0') === selectedSearchResult.product_code) {
              additional = true;
              return true;
            }
          });
        }),
      );

      if (!additional) {
        navigation.navigate(AppRoute.PRODUCT, {
          product: selectedSearchResult,
        });
      }
    }, [
      activationsStore.promotions,
      brandsStore.brands,
      feedStore.discoverList,
      globalStore,
      globalStore.isFromNavigationSearch,
      navigation,
      selectedSearchResult,
    ]);

    useFocusEffect(
      React.useCallback(() => {
        setSelectedSearchResult(null);
        return () => null;
      }, []),
    );

    const searchMFLKeywords = (text: string) => {
      //Check if there are any previous pending requests
      if (typeof cancelToken !== typeof undefined) {
        cancelToken.cancel('Operation canceled due to new request.');
      }

      //Save the cancel token for the current request
      cancelToken = axios.CancelToken.source();
      let reqIndex = ++reqCount;

      if (!!text && text.length >= 3) {
        setIsSearching(true);
        apiService
          .searchProductsKeyword(text, cancelToken)
          .then(res => {
            setIsSearching(false);
            setHasSearched(true);
            const allResults: any[] = [];
            if (config.FF_IS_GIFT_CARDS_ENABLED) {
              if (text.toLowerCase().includes('gif')) {
                allResults.push({
                  type: GIFT_CARD_RESULT_TYPE,
                });
              }
            }
            if (res && reqIndex === reqCount) {
              allResults.concat(res);
              setResults(allResults.concat(res));
            }
          })
          .catch(err => {
            if (!axios.isCancel(err)) {
              setIsSearching(false);
            }
          });
      } else {
        setIsSearching(false);
        setHasSearched(false);
        console.log('no text to search');
        setResults([]);
      }
    };

    const handleManualSearchPress = () => {
      if (searchText && searchText.length >= 3) {
        setResults([]);
        setSelectedSearchResult(null);
        searchMFLKeywords(searchText);
      } else if (scannedBarcodeValue) {
        setResults([]);
        setSelectedSearchResult(null);
        searchMflByBarcodeValue(scannedBarcodeValue);
      }
    };

    const filterTextInput = (text: string) => {
      // Remove anything that's not alphanumeric, space or hyphen, &.
      const filter = /[^0-9a-zA-Z- '&]/gi;
      const newTerm = text.replace(filter, '');
      return newTerm;
    };

    const handleTextChanged = (text: string) => {
      setHasSearched(false);
      setIsBarcode(false);
      const newTerm = filterTextInput(text);
      // Avoid API call for the same term when the user is writing
      if (newTerm !== searchText) {
        if (selectedSearchResult) {
          setSelectedSearchResult(null);
        }
        setResults([]);
        setScannedBarcodeValue('');
        setSearchText(newTerm);
        // shopSelectionStore.getSuburbList(newTerm);
      }
    };

    const handleTextSubmitEditing = ({
      nativeEvent,
    }: {
      nativeEvent: {text: string};
    }) => {
      const {text} = nativeEvent;
      setHasSearched(false);
      setIsBarcode(false);
      const newTerm = filterTextInput(text);
      // Always call the API when the user press submit
      if (selectedSearchResult) {
        setSelectedSearchResult(null);
      }
      setResults([]);
      setScannedBarcodeValue('');
      setSearchText(newTerm);
      searchMFLKeywords(debouncedSearchText);
    };

    const handleClearSearchPress = () => {
      setIsBarcode(false);
      handleTextChanged('');
      setScannedBarcodeValue('');
    };

    const searchMflByBarcodeValue = (barcodeValue: string) => {
      setIsSearching(true);
      apiService
        .searchByBarcode([barcodeValue])
        .then(res => {
          setIsSearching(false);
          setHasSearched(true);
          if (res?.length) {
            setSelectedSearchResult(res[0]);
          } else {
            setSelectedSearchResult(null);
          }
        })
        .catch(err => {
          setIsSearching(false);
          setHasSearched(true);
          console.error('Error searching Mfl by barcode', err);
        });
    };

    const handleBarcodeRead = (barcodes: {displayValue: string}[]) => {
      setHasSearched(false);
      setIsBarcode(true);
      if (barcodes.length && barcodes[0].displayValue) {
        const barcodeValue = barcodes[0].displayValue;
        setScannedBarcodeValue(barcodeValue);
        setSearchText('');
        setScanning(false);
        searchMflByBarcodeValue(barcodeValue);
      } else {
        setIsSearching(false);
        setHasSearched(true);
        console.log('no barcode search results');
        setResults([]);
      }
    };

    const handleProductTilePress = (item: any) => {
      Keyboard.dismiss();
      setSelectedSearchResult(item);
    };

    const renderItem = ({item, index}: {item: any; index: number}) => (
      <Animated.View
        entering={SlideInLeft.delay(index * 100).duration(500)}
        exiting={SlideOutRight.delay(index * 10).duration(150)}>
        {item.type === GIFT_CARD_RESULT_TYPE ? (
          <GiftCardTileAbbreviatedHorizontal />
        ) : (
          <ProductTileAbbreviatedHorizontal
            item={item}
            index={index}
            pressCallback={handleProductTilePress}
          />
        )}
      </Animated.View>
    );

    const renderSearchResults = () => {
      if (selectedSearchResult) return null;

      return (
        <FlatList
          data={results}
          renderItem={renderItem}
          keyExtractor={(item, index) => `${item.id}-${index}`}
          ItemSeparatorComponent={() => <View style={{marginTop: 8}} />}
          style={styles.primaryListWrapper}
          keyboardShouldPersistTaps="always"
          keyboardDismissMode="on-drag"
          ListEmptyComponent={() => {
            if (hasSearched && !isSearching) {
              return !isBarcode ? (
                <FullScreenMessageView>NO PRODUCTS FOUND</FullScreenMessageView>
              ) : (
                <FullScreenMessageView>
                  Sorry, this product is not available.
                </FullScreenMessageView>
              );
            }
            if (isSearching) {
              return (
                <FullScreenMessageView>SEARCHING...</FullScreenMessageView>
              );
            }
            return null;
          }}
        />
      );
    };

    const renderSelectedSearchResult = () => {
      if (!selectedSearchResult) return null;

      return (
        <Animated.View
          entering={SlideInDown.delay(100).duration(500)}
          exiting={SlideOutDown.duration(100)}
          key={selectedSearchResult.id}>
          <ProductTileAbbreviatedHorizontal
            item={selectedSearchResult}
            index={0}
          />
        </Animated.View>
      );
    };

    const renderActivationResults = () => {
      if (activations.length === 0) return null;

      return (
        <Animated.View
          entering={SlideInDown.duration(500).delay(300)}
          exiting={SlideOutDown.duration(300)}
          style={styles.section}>
          <Text style={styles.sectionHeader}>Featured in Promotions:</Text>
          {activations.map(activation => (
            <PromotionSearchAbbreviatedTile
              item={activation}
              key={activation.id}
            />
          ))}
        </Animated.View>
      );
    };

    const renderDiscoverResults = () => {
      if (activations.length === 0) return null;

      return (
        <Animated.View
          entering={SlideInDown.duration(500).delay(350)}
          exiting={SlideOutDown.duration(300)}
          style={styles.section}>
          <Text style={styles.sectionHeader}>Found in Articles:</Text>
          {discoverArticles.map(article => (
            <Fragment key={article.id}>
              <SearchDiscoverAbbreviatedTile item={article} key={article.id} />
              <View style={{marginBottom: 8}} />
            </Fragment>
          ))}
        </Animated.View>
      );
    };

    const renderBrandResults = () => {
      if (!brand) return null;

      return (
        <Animated.View
          entering={SlideInDown.duration(500).delay(500)}
          exiting={SlideOutDown.duration(300)}
          style={styles.section}>
          <Text style={styles.sectionHeader}>Featured Brand:</Text>
          <View>
            <SearchBrandAbbreviatedTile item={brand} />
          </View>
        </Animated.View>
      );
    };

    const renderAdditionalResults = () => (
      <Animated.View
        entering={SlideInDown.duration(500).delay(500)}
        exiting={SlideOutRight.duration(300)}
        style={styles.secondaryListWrapper}>
        <ScrollView showsVerticalScrollIndicator={false}>
          {renderSelectedSearchResult()}
          {renderActivationResults()}
          {renderDiscoverResults()}
          {renderBrandResults()}
        </ScrollView>
      </Animated.View>
    );

    const userInput = searchText || scannedBarcodeValue;
    // TODO: Consider changing this into one section list and changing the data on the fly.
    // That way we can probably reuse the same element and have it neatly apply to the top
    // without having it disappear and re-enter from the bottom of the screen.
    return (
      <ContentView>
        <SearchBar
          value={userInput}
          onManualSearchPress={handleManualSearchPress}
          onTextChanged={handleTextChanged}
          onSubmitEditing={handleTextSubmitEditing}
          onClearSearchPress={handleClearSearchPress}
          onScanBarcodePress={() => setScanning(true)}
          editable={!scanning}
        />
        {renderSearchResults()}
        {renderAdditionalResults()}
        <Modal animationType="slide" transparent={true} visible={scanning}>
          <View style={{flex: 1}}>
            <BarcodeScanner
              onRead={handleBarcodeRead}
              onCancel={() => setScanning(false)}
            />
          </View>
        </Modal>
      </ContentView>
    );
  },
);

export default SearchScreen;
